import React, { useEffect, useMemo, useState } from "react";
import { http } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/* -----------------------------------------------------------------------
 * Developer settings — manage API keys and webhook subscriptions.
 *
 * Two tabs:
 *   API KEYS  — create/list/revoke. Plaintext shown ONCE in a reveal modal.
 *   WEBHOOKS  — owner-scoped to a league. Pick a league → manage its
 *               subscriptions, fire a test delivery, inspect deliveries.
 *
 * Auth model: cookie session. The user must already be logged in.
 * --------------------------------------------------------------------- */

// ---------- shared UI primitives -------------------------------------------

const SectionCard = ({ children, className = "", ...rest }) => (
  <div className={`glass rounded-2xl p-5 ${className}`} {...rest}>
    {children}
  </div>
);

const SmallButton = ({ variant = "ghost", className = "", ...rest }) => {
  const base = "text-[11px] tracking-widest px-3 py-2 rounded-lg transition-colors";
  const cls =
    variant === "primary"
      ? "btn-neon"
      : variant === "danger"
      ? "border border-[#E8A857]/55 text-[#E8A857] hover:bg-[#E8A857]/10 bg-transparent"
      : "btn-ghost";
  return <button type="button" className={`${base} ${cls} ${className}`} {...rest} />;
};

function Pill({ children, color = "#14b8a6", testid }) {
  return (
    <span
      data-testid={testid}
      className="inline-block px-2 py-0.5 rounded-md text-[10px] font-mono tracking-wider"
      style={{
        backgroundColor: `${color}1f`,
        border: `1px solid ${color}55`,
        color,
      }}
    >
      {children}
    </span>
  );
}

function CopyButton({ text, testid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 1300);
        } catch {
          /* clipboard blocked — ignore */
        }
      }}
      className="text-[10px] tracking-widest px-2 py-1 rounded-md border border-[rgba(20,184,166,0.30)] text-white/85 hover:text-[#14b8a6] hover:border-[#14b8a6]/60"
    >
      {done ? "COPIED" : "COPY"}
    </button>
  );
}

// =============================================================================
// Tab 1 — API KEYS
// =============================================================================

function ApiKeysTab() {
  const [keys, setKeys] = useState([]);
  const [scopesCatalog, setScopesCatalog] = useState({});
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [pickedScopes, setPickedScopes] = useState(new Set(["leagues:read"]));
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState(null); // {key, ...}
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const [a, b] = await Promise.all([
        http.get("/v1/api-keys"),
        http.get("/v1/api-keys/scopes"),
      ]);
      setKeys(a.data || []);
      setScopesCatalog(b.data?.scopes || {});
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load keys.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleScope = (s) => {
    setPickedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const create = async () => {
    if (!name.trim()) { setErr("Pick a name first."); return; }
    if (pickedScopes.size === 0) { setErr("Pick at least one scope."); return; }
    setCreating(true);
    setErr("");
    try {
      const { data } = await http.post("/v1/api-keys", {
        name: name.trim(),
        scopes: [...pickedScopes],
      });
      setRevealed(data); // includes plaintext `.key`
      setName("");
      setPickedScopes(new Set(["leagues:read"]));
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to create key.");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (key_id) => {
    if (!window.confirm("Revoke this key? Active integrations will stop working immediately.")) return;
    await http.delete(`/v1/api-keys/${key_id}`);
    await load();
  };

  return (
    <div className="space-y-6" data-testid="api-keys-tab">
      <SectionCard data-testid="api-keys-create-card">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-semibold mb-3">
          CREATE A NEW KEY
        </p>
        <input
          type="text"
          placeholder="Name (e.g. Discord bot)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="api-key-name-input"
          className="w-full bg-black/30 border border-[rgba(20,184,166,0.25)] focus:border-[#14b8a6]/60 focus:outline-none rounded-lg px-3 py-2 text-sm text-white placeholder-white/40 mb-3"
        />
        <p className="text-[10px] tracking-widest text-white/55 mb-2">SCOPES</p>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="api-key-scope-row">
          {Object.entries(scopesCatalog).map(([s, desc]) => {
            const active = pickedScopes.has(s);
            return (
              <button
                key={s}
                type="button"
                title={desc}
                data-testid={`scope-${s}`}
                onClick={() => toggleScope(s)}
                className={`text-[10px] font-mono px-2 py-1 rounded-md transition-colors border ${
                  active
                    ? "bg-[#14b8a6]/20 text-[#14b8a6] border-[#14b8a6]/60"
                    : "border-[rgba(20,184,166,0.20)] text-white/65 hover:text-white"
                }`}
              >
                {s}
              </button>
            );
          })}
        </div>
        {err && <p className="text-[12px] text-[#E8A857] mb-3" data-testid="api-key-err">{err}</p>}
        <SmallButton
          variant="primary"
          onClick={create}
          disabled={creating}
          data-testid="api-key-create-btn"
        >
          {creating ? "CREATING…" : "MINT KEY"}
        </SmallButton>
      </SectionCard>

      <SectionCard data-testid="api-keys-list-card">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-semibold mb-3">
          YOUR KEYS · {keys.length}
        </p>
        {loading ? (
          <p className="text-[12px] text-white/55">Loading…</p>
        ) : keys.length === 0 ? (
          <p className="text-[12px] text-white/55">No keys yet — mint your first above.</p>
        ) : (
          <div className="space-y-2">
            {keys.map((k) => (
              <div
                key={k.key_id}
                data-testid={`api-key-row-${k.key_id}`}
                className="flex items-center gap-3 rounded-lg border border-[rgba(20,184,166,0.18)] bg-black/20 px-3 py-2.5"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-white text-[13px] font-semibold truncate">
                      {k.name}
                    </p>
                    {!k.is_active && <Pill color="#a3a3ac">REVOKED</Pill>}
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <code className="text-[11px] font-mono text-white/55">
                      {k.key_prefix}…
                    </code>
                    {(k.scopes || []).map((s) => (
                      <Pill key={s}>{s}</Pill>
                    ))}
                  </div>
                </div>
                {k.is_active && (
                  <SmallButton
                    variant="danger"
                    onClick={() => revoke(k.key_id)}
                    data-testid={`api-key-revoke-${k.key_id}`}
                  >
                    REVOKE
                  </SmallButton>
                )}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {revealed && (
        <RevealKeyModal
          revealed={revealed}
          onClose={() => setRevealed(null)}
        />
      )}
    </div>
  );
}

function RevealKeyModal({ revealed, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm"
      data-testid="api-key-reveal-modal"
      onClick={onClose}
    >
      <div
        className="glass rounded-2xl p-6 max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-semibold mb-2">
          ONE-TIME SECRET
        </p>
        <p className="text-white text-lg font-semibold mb-1">
          Copy your key now
        </p>
        <p className="text-[12.5px] text-[#a3a3ac] mb-4">
          This is the only time the plaintext key will be shown. We store
          only a SHA-256 hash — there&apos;s no way to retrieve it later.
        </p>
        <div className="rounded-lg border border-[rgba(20,184,166,0.30)] bg-black/40 px-3 py-2 flex items-center gap-2 mb-4">
          <code
            data-testid="api-key-reveal-plaintext"
            className="text-[12.5px] font-mono text-white flex-1 break-all"
          >
            {revealed.key}
          </code>
          <CopyButton text={revealed.key} testid="api-key-reveal-copy" />
        </div>
        <SmallButton
          variant="primary"
          onClick={onClose}
          data-testid="api-key-reveal-close"
        >
          I&apos;VE COPIED IT
        </SmallButton>
      </div>
    </div>
  );
}

// =============================================================================
// Tab 2 — WEBHOOKS (per-league)
// =============================================================================

function WebhooksTab() {
  const [leagues, setLeagues] = useState([]);
  const [leagueId, setLeagueId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await http.get("/leagues/mine");
        const owned = (data || []).filter((lg) => lg.is_owner);
        setLeagues(owned);
        if (owned.length === 1) setLeagueId(owned[0].league_id);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <p className="text-[12px] text-white/55">Loading your leagues…</p>;
  }
  if (leagues.length === 0) {
    return (
      <SectionCard data-testid="webhooks-empty-card">
        <p className="text-white text-[14px] mb-1">No leagues to manage.</p>
        <p className="text-[12.5px] text-[#a3a3ac]">
          Webhooks are owner-scoped. Create a league first to start
          subscribing external tools to its events.
        </p>
      </SectionCard>
    );
  }
  return (
    <div className="space-y-5" data-testid="webhooks-tab">
      <SectionCard>
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-semibold mb-3">
          PICK A LEAGUE
        </p>
        <select
          value={leagueId || ""}
          onChange={(e) => setLeagueId(e.target.value || null)}
          data-testid="webhooks-league-picker"
          className="w-full bg-black/30 border border-[rgba(20,184,166,0.25)] focus:border-[#14b8a6]/60 focus:outline-none rounded-lg px-3 py-2 text-sm text-white"
        >
          <option value="">— Select —</option>
          {leagues.map((lg) => (
            <option key={lg.league_id} value={lg.league_id}>
              {lg.name} · {lg.game}
            </option>
          ))}
        </select>
      </SectionCard>
      {leagueId && <LeagueWebhooksPanel leagueId={leagueId} />}
    </div>
  );
}

function LeagueWebhooksPanel({ leagueId }) {
  const [hooks, setHooks] = useState([]);
  const [eventCatalog, setEventCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState("");
  const [selected, setSelected] = useState(new Set(["league.match.result"]));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [revealed, setRevealed] = useState(null); // {webhook_id, secret, ...}
  const [openDeliveries, setOpenDeliveries] = useState(null);
  // Inline test-result state per webhook_id: { status: "queued"|"polling"|"resolved"|"error",
  //   delivery_id, result: {status, response_status, error}, ts }
  const [testResults, setTestResults] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        http.get(`/v1/leagues/${leagueId}/webhooks`),
        http.get(`/v1/leagues/${leagueId}/webhooks/events`),
      ]);
      setHooks(a.data || []);
      setEventCatalog(b.data?.events || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leagueId]);

  const toggle = (ev) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(ev) ? next.delete(ev) : next.add(ev);
      return next;
    });

  const create = async () => {
    if (!url.trim()) { setErr("Receiver URL required."); return; }
    if (selected.size === 0) { setErr("Pick at least one event."); return; }
    setBusy(true);
    setErr("");
    try {
      const { data } = await http.post(`/v1/leagues/${leagueId}/webhooks`, {
        url: url.trim(),
        events: [...selected],
      });
      setRevealed(data);
      setUrl("");
      setSelected(new Set(["league.match.result"]));
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to create webhook.");
    } finally {
      setBusy(false);
    }
  };

  const setEnabled = async (wid, enabled) => {
    await http.patch(`/v1/leagues/${leagueId}/webhooks/${wid}`, { enabled });
    await load();
  };
  const remove = async (wid) => {
    if (!window.confirm("Delete this webhook subscription?")) return;
    await http.delete(`/v1/leagues/${leagueId}/webhooks/${wid}`);
    await load();
  };
  const fireTest = async (wid) => {
    // Optimistically mark this row as queued so the inline pill flips
    // immediately on click — no alert dialog, no jarring round-trip.
    setTestResults((prev) => ({
      ...prev,
      [wid]: { status: "queued", delivery_id: null, ts: Date.now() },
    }));
    let delivery_id = null;
    try {
      const { data } = await http.post(
        `/v1/leagues/${leagueId}/webhooks/${wid}/test`,
      );
      delivery_id = data?.delivery_id || null;
      setTestResults((prev) => ({
        ...prev,
        [wid]: { status: "polling", delivery_id, ts: Date.now() },
      }));
    } catch (e) {
      setTestResults((prev) => ({
        ...prev,
        [wid]: {
          status: "error",
          error: e.response?.data?.detail || "Test failed.",
          ts: Date.now(),
        },
      }));
      return;
    }
    // Poll the deliveries log for the row we just queued. Up to 5 tries
    // over ~6s — first attempt fires at +2s to let the worker complete.
    const tryFetch = async (attempt) => {
      try {
        const { data } = await http.get(
          `/v1/leagues/${leagueId}/webhooks/${wid}/deliveries`,
          { params: { limit: 5 } },
        );
        const found = (data?.deliveries || []).find(
          (d) => d.delivery_id === delivery_id,
        );
        if (found && found.status !== "pending") {
          setTestResults((prev) => ({
            ...prev,
            [wid]: {
              status: "resolved",
              delivery_id,
              result: found,
              ts: Date.now(),
            },
          }));
          // Refresh the parent hooks list so any side-effects show up.
          load();
          return true;
        }
      } catch { /* swallow — try again */ }
      return false;
    };
    for (let i = 1; i <= 5; i++) {
      // 2s, 3s, 4.5s, 6s, 7.5s — generous enough that 95% of receivers
      // resolve inside the polling window.
      await new Promise((r) => setTimeout(r, i === 1 ? 2000 : 1500));
      if (await tryFetch(i)) return;
    }
    // Out of attempts — leave the pill in "polling" state with a nudge
    // to open the full Deliveries modal for the live status.
    setTestResults((prev) => ({
      ...prev,
      [wid]: {
        ...(prev[wid] || {}),
        status: "timeout",
        delivery_id,
        ts: Date.now(),
      },
    }));
  };
  const clearTestResult = (wid) =>
    setTestResults((prev) => {
      const next = { ...prev };
      delete next[wid];
      return next;
    });

  return (
    <>
      <SectionCard data-testid="webhooks-create-card">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-semibold mb-3">
          NEW SUBSCRIPTION
        </p>
        <input
          type="url"
          placeholder="https://your-server.example/webhook"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          data-testid="webhook-url-input"
          className="w-full bg-black/30 border border-[rgba(20,184,166,0.25)] focus:border-[#14b8a6]/60 focus:outline-none rounded-lg px-3 py-2 text-sm text-white placeholder-white/40 mb-3"
        />
        <p className="text-[10px] tracking-widest text-white/55 mb-2">EVENTS</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {eventCatalog.map((ev) => {
            const active = selected.has(ev);
            return (
              <button
                key={ev}
                type="button"
                data-testid={`webhook-event-${ev}`}
                onClick={() => toggle(ev)}
                className={`text-[10px] font-mono px-2 py-1 rounded-md transition-colors border ${
                  active
                    ? "bg-[#14b8a6]/20 text-[#14b8a6] border-[#14b8a6]/60"
                    : "border-[rgba(20,184,166,0.20)] text-white/65 hover:text-white"
                }`}
              >
                {ev}
              </button>
            );
          })}
        </div>
        {err && <p className="text-[12px] text-[#E8A857] mb-3">{err}</p>}
        <SmallButton variant="primary" onClick={create} disabled={busy} data-testid="webhook-create-btn">
          {busy ? "CREATING…" : "CREATE WEBHOOK"}
        </SmallButton>
      </SectionCard>

      <SectionCard data-testid="webhooks-list-card">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-semibold mb-3">
          ACTIVE WEBHOOKS · {hooks.length}
        </p>
        {loading ? (
          <p className="text-[12px] text-white/55">Loading…</p>
        ) : hooks.length === 0 ? (
          <p className="text-[12px] text-white/55">No webhooks yet.</p>
        ) : (
          <div className="space-y-2">
            {hooks.map((w) => (
              <div
                key={w.webhook_id}
                data-testid={`webhook-row-${w.webhook_id}`}
                className="rounded-lg border border-[rgba(20,184,166,0.18)] bg-black/20 px-3 py-2.5"
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <code className="text-[12px] font-mono text-white/90 flex-1 min-w-0 truncate">
                    {w.url}
                  </code>
                  {w.enabled ? (
                    <Pill color="#22c55e">ENABLED</Pill>
                  ) : (
                    <Pill color="#a3a3ac">DISABLED</Pill>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(w.events || []).map((e) => (
                    <Pill key={e}>{e}</Pill>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <SmallButton
                    onClick={() => fireTest(w.webhook_id)}
                    data-testid={`webhook-test-${w.webhook_id}`}
                  >
                    TEST FIRE
                  </SmallButton>
                  <SmallButton
                    onClick={() => setOpenDeliveries(w.webhook_id)}
                    data-testid={`webhook-deliveries-${w.webhook_id}`}
                  >
                    DELIVERIES
                  </SmallButton>
                  <SmallButton
                    onClick={() => setEnabled(w.webhook_id, !w.enabled)}
                    data-testid={`webhook-toggle-${w.webhook_id}`}
                  >
                    {w.enabled ? "DISABLE" : "ENABLE"}
                  </SmallButton>
                  <SmallButton
                    variant="danger"
                    onClick={() => remove(w.webhook_id)}
                    data-testid={`webhook-delete-${w.webhook_id}`}
                  >
                    DELETE
                  </SmallButton>
                </div>
                {testResults[w.webhook_id] && (
                  <InlineTestResult
                    result={testResults[w.webhook_id]}
                    onClear={() => clearTestResult(w.webhook_id)}
                    onOpenLog={() => setOpenDeliveries(w.webhook_id)}
                    testid={`webhook-test-result-${w.webhook_id}`}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {revealed && (
        <RevealSecretModal revealed={revealed} onClose={() => setRevealed(null)} />
      )}
      {openDeliveries && (
        <DeliveriesModal
          leagueId={leagueId}
          webhookId={openDeliveries}
          onClose={() => setOpenDeliveries(null)}
        />
      )}
    </>
  );
}

function RevealSecretModal({ revealed, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm"
      data-testid="webhook-reveal-modal"
      onClick={onClose}
    >
      <div
        className="glass rounded-2xl p-6 max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-semibold mb-2">
          ONE-TIME SECRET
        </p>
        <p className="text-white text-lg font-semibold mb-1">Copy your secret now</p>
        <p className="text-[12.5px] text-[#a3a3ac] mb-4">
          Use this to verify the <code className="text-[#14b8a6]">X-LeagueHub-Signature</code>{" "}
          header on every delivery. We store only a hash — there&apos;s no way
          to retrieve it later.
        </p>
        <div className="rounded-lg border border-[rgba(20,184,166,0.30)] bg-black/40 px-3 py-2 flex items-center gap-2 mb-4">
          <code
            data-testid="webhook-reveal-secret"
            className="text-[12.5px] font-mono text-white flex-1 break-all"
          >
            {revealed.secret}
          </code>
          <CopyButton text={revealed.secret} testid="webhook-reveal-copy" />
        </div>
        <SmallButton variant="primary" onClick={onClose} data-testid="webhook-reveal-close">
          I&apos;VE COPIED IT
        </SmallButton>
      </div>
    </div>
  );
}

function DeliveriesModal({ leagueId, webhookId, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get(
        `/v1/leagues/${leagueId}/webhooks/${webhookId}/deliveries`,
        { params: status ? { status, limit: 50 } : { limit: 50 } },
      );
      setRows(data?.deliveries || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, webhookId]);

  const COLORS = {
    delivered: "#22c55e", retrying: "#E8A857",
    failed: "#ef4444", pending: "#a3a3ac",
  };
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm"
      data-testid="webhook-deliveries-modal"
      onClick={onClose}
    >
      <div
        className="glass rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <p className="text-white text-base font-semibold">Recent deliveries</p>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="webhook-deliveries-status-filter"
            className="bg-black/30 border border-[rgba(20,184,166,0.25)] rounded-lg px-2 py-1 text-[11px] text-white"
          >
            <option value="">All</option>
            <option value="delivered">Delivered</option>
            <option value="retrying">Retrying</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        {loading ? (
          <p className="text-[12px] text-white/55">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-[12px] text-white/55">No deliveries yet.</p>
        ) : (
          <div className="space-y-2">
            {rows.map((d) => (
              <div
                key={d.delivery_id}
                data-testid={`delivery-${d.delivery_id}`}
                className="rounded-lg border border-[rgba(20,184,166,0.18)] bg-black/25 px-3 py-2"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <Pill color={COLORS[d.status] || "#a3a3ac"}>
                    {d.status.toUpperCase()}
                  </Pill>
                  <code className="text-[11px] font-mono text-white/85">{d.event}</code>
                  <span className="text-[11px] text-white/55 ml-auto">
                    {new Date(d.created_at).toLocaleString()}
                  </span>
                </div>
                <p className="text-[11px] text-white/60 mt-1">
                  attempts: {d.attempts}
                  {d.response_status ? ` · http ${d.response_status}` : ""}
                  {d.error ? ` · ${d.error}` : ""}
                </p>
              </div>
            ))}
          </div>
        )}
        <div className="mt-5 text-right">
          <SmallButton onClick={onClose} data-testid="webhook-deliveries-close">CLOSE</SmallButton>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Inline test-result chip + PRO gate
// =============================================================================

function InlineTestResult({ result, onClear, onOpenLog, testid }) {
  const { status, result: row, error } = result || {};
  let pillColor = "#14b8a6";
  let label = "";
  let detail = "";
  if (status === "queued") {
    label = "QUEUED";
    detail = "Synthetic delivery enqueued — waiting for receiver…";
  } else if (status === "polling") {
    label = "POLLING…";
    detail = "Fetching delivery status from the log.";
  } else if (status === "timeout") {
    pillColor = "#E8A857";
    label = "STILL PENDING";
    detail = "Receiver hasn't acked yet. Open the Deliveries log to keep watching.";
  } else if (status === "error") {
    pillColor = "#E8A857";
    label = "ERROR";
    detail = error || "Test failed.";
  } else if (status === "resolved" && row) {
    const s = (row.status || "").toLowerCase();
    if (s === "delivered") { pillColor = "#22c55e"; label = "DELIVERED"; }
    else if (s === "retrying") { pillColor = "#E8A857"; label = "RETRYING"; }
    else if (s === "failed") { pillColor = "#E8A857"; label = "FAILED"; }
    else { pillColor = "#a3a3ac"; label = (s || "UNKNOWN").toUpperCase(); }
    detail = [
      row.response_status ? `http ${row.response_status}` : null,
      `attempt ${row.attempts}`,
      row.error || null,
    ].filter(Boolean).join(" · ");
  }
  return (
    <div
      data-testid={testid}
      className="mt-3 rounded-lg border border-[rgba(20,184,166,0.25)] bg-black/30 px-3 py-2 flex items-start gap-3"
    >
      <span
        className="inline-block px-2 py-0.5 rounded-md text-[10px] font-mono tracking-wider shrink-0"
        style={{
          backgroundColor: `${pillColor}1f`,
          border: `1px solid ${pillColor}55`,
          color: pillColor,
        }}
      >
        {(status === "queued" || status === "polling") && (
          <i className="fas fa-circle-notch fa-spin mr-1" />
        )}
        {label}
      </span>
      <p className="text-[11px] text-white/75 leading-snug flex-1 min-w-0 break-words">
        {detail}
      </p>
      <div className="flex items-center gap-1 shrink-0">
        {(status === "resolved" || status === "timeout") && (
          <button
            type="button"
            onClick={onOpenLog}
            className="text-[10px] tracking-widest text-[#14b8a6] hover:underline"
            data-testid={`${testid}-open-log`}
          >
            LOG
          </button>
        )}
        <button
          type="button"
          onClick={onClear}
          className="text-[10px] tracking-widest text-white/55 hover:text-white"
          data-testid={`${testid}-dismiss`}
        >
          ×
        </button>
      </div>
    </div>
  );
}

function ProGate({ user }) {
  if (!user) return null;
  const isPro =
    user.user_type === "pro" ||
    user.plan === "pro" || user.plan === "elite" ||
    user.paid_pro_active === true;
  if (isPro) return null;
  return (
    <SectionCard
      data-testid="dev-settings-pro-gate"
      className="border border-[rgba(20,184,166,0.45)]"
    >
      <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-semibold mb-2">
        PRO PERK · LOCKED
      </p>
      <p className="text-white text-lg font-semibold mb-1">
        Developer access is a PRO perk.
      </p>
      <p className="text-[12.5px] text-[#a3a3ac] mb-4">
        Mint API keys, subscribe to webhooks, and pipe live league data
        into your own tools. Available on PRO Player ($1.99/mo) and
        PRO League ($9.99/mo). API access automatically expires the
        moment your subscription lapses — no surprise bills, no rogue
        bots after a cancel.
      </p>
      <a
        href="/pricing"
        data-testid="dev-settings-pro-upgrade-cta"
        className="btn-neon inline-block text-[11px] tracking-widest px-4 py-2 rounded-lg"
      >
        SEE PRO PLANS
      </a>
    </SectionCard>
  );
}

// =============================================================================

export default function DeveloperSettingsPage() {
  const [tab, setTab] = useState("keys");
  const { user } = useAuth();
  const isPro =
    !!user && (
      user.user_type === "pro" ||
      user.plan === "pro" || user.plan === "elite" ||
      user.paid_pro_active === true
    );

  const tabs = useMemo(() => ([
    { id: "keys",     label: "API KEYS" },
    { id: "webhooks", label: "WEBHOOKS" },
  ]), []);

  return (
    <main
      className="max-w-screen-md mx-auto px-5 lg:px-8 pt-10 pb-24 page-z"
      data-testid="dev-settings-page"
    >
      <header className="mb-8">
        <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-semibold mb-2">
          DEVELOPER SETTINGS
        </p>
        <h1 className="font-display text-3xl md:text-4xl text-white tracking-[2px]"
            style={{ textShadow: "none" }}>
          API KEYS & WEBHOOKS
        </h1>
        <p className="mt-3 text-[14px] text-[#a3a3ac]">
          Build with the LEAGUE HUB API. See the{" "}
          <a href="/developers" className="text-[#14b8a6] hover:underline">developer portal</a>
          {" "}for endpoint docs and code examples.
        </p>
      </header>

      <div className="flex gap-1 mb-5">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`dev-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-[11px] font-semibold tracking-widest uppercase transition-colors border ${
              tab === t.id
                ? "bg-[rgba(20,184,166,0.18)] text-white border-[rgba(20,184,166,0.55)]"
                : "border-transparent text-white/65 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!user ? (
        <SectionCard data-testid="dev-settings-anon">
          <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-semibold mb-2">SIGN IN REQUIRED</p>
          <p className="text-[12.5px] text-[#a3a3ac]">
            Sign in with Google or Discord to access the developer surface.
          </p>
        </SectionCard>
      ) : !isPro ? (
        <ProGate user={user} />
      ) : (tab === "keys" ? <ApiKeysTab /> : <WebhooksTab />)}
    </main>
  );
}
